import React, { useState, useCallback, useRef } from 'react';
import { X, Upload, FileVideo, FileSpreadsheet, Loader2, Clock, CheckCircle2 } from 'lucide-react';
import { getAuthToken } from '../../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AddGameModal — Universal game upload modal for basketball intelligence dashboards.
 *
 * Accepts video (MP4) + Synergy (CSV/XML) uploads, starts the analysis pipeline,
 * and provides real-time progress feedback.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   gameSet: string — The game set to register the game under
 *   onGameAdded: (game) => void — Called when game is registered and pipeline starts
 */
export default function AddGameModal({ isOpen, onClose, gameSet, onGameAdded }) {
  const [videoFile, setVideoFile] = useState(null);
  const [synergyFile, setSynergyFile] = useState(null);
  const [gameName, setGameName] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState(''); // 'uploading_video' | 'registering' | 'done'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const videoInputRef = useRef(null);
  const synergyInputRef = useRef(null);

  const handleVideoDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0] || e.target?.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      setError(null);
      // Auto-fill game name from filename
      if (!gameName) {
        const name = file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
        setGameName(name);
      }
    } else if (file) {
      setError('Please upload an MP4 video file');
    }
  }, [gameName]);

  const handleSynergyDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0] || e.target?.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (['csv', 'xml'].includes(ext)) {
        setSynergyFile(file);
        setError(null);
      } else {
        setError('Please upload a CSV or XML Synergy export');
      }
    }
  }, []);

  const generateGameId = () => {
    if (!gameName) return '';
    // Convert "Cornell @ Princeton" → "CORNELL_AT_PRINCETON"
    const base = gameName
      .toUpperCase()
      .replace(/@/g, 'AT')
      .replace(/\s+vs\.?\s+/gi, '_VS_')
      .replace(/\s+at\s+/gi, '_AT_')
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const datePart = gameDate
      ? new Date(gameDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '.')
      : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '.');
    return `${base}_${datePart}`;
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
        URL.revokeObjectURL(video.src);
      };
      video.onerror = () => resolve(0);
      video.src = URL.createObjectURL(file);
    });
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const handleSubmit = async () => {
    if (!gameName) {
      setError('Game name is required');
      return;
    }
    if (!videoFile && !synergyFile) {
      setError('Please upload at least a video or Synergy file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const gameId = generateGameId();
      let videoGcsPath = null;
      let videoDuration = 0;

      // Step 1: Upload video to GCS via signed URL (if provided)
      if (videoFile) {
        setUploadPhase('uploading_video');
        videoDuration = await getVideoDuration(videoFile);

        // Get signed upload URL
        const authHeaders = {};
        const authToken = getAuthToken();
        if (authToken) authHeaders.Authorization = authToken;
        const urlRes = await fetch(
          `${BACKEND_URL}/api/game-intelligence/upload-url?` +
          `filename=${encodeURIComponent(videoFile.name)}&gameId=${encodeURIComponent(gameId)}`,
          { headers: authHeaders }
        );
        const urlData = await urlRes.json();

        if (!urlData.success) {
          throw new Error(urlData.error || 'Failed to get upload URL');
        }

        // Upload video directly to GCS with progress tracking
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.open('PUT', urlData.sessionUrl);
          xhr.setRequestHeader('Content-Type', videoFile.type || 'video/mp4');
          xhr.send(videoFile);
        });

        videoGcsPath = urlData.gcsPath;
      }

      // Step 2: Register game + upload Synergy data + start pipeline
      setUploadPhase('registering');
      const formData = new FormData();
      formData.append('gameId', gameId);
      formData.append('name', gameName);
      formData.append('date', gameDate || new Date().toISOString().slice(0, 10));
      formData.append('homeTeam', homeTeam);
      formData.append('awayTeam', awayTeam);
      formData.append('gameSet', gameSet);
      formData.append('videoDuration', String(videoDuration));
      if (videoGcsPath) {
        formData.append('videoGcsPath', videoGcsPath);
      }
      if (synergyFile) {
        formData.append('synergy', synergyFile);
      }

      const uploadHeaders = {};
      const uploadToken = getAuthToken();
      if (uploadToken) uploadHeaders.Authorization = uploadToken;
      const res = await fetch(`${BACKEND_URL}/api/game-intelligence/upload`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Registration failed');
      }

      setUploadPhase('done');
      if (onGameAdded) {
        onGameAdded(data.game);
      }

      // Close modal after brief success display
      setTimeout(() => {
        onClose();
        resetForm();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setUploading(false);
      setUploadPhase('');
    }
  };

  const resetForm = () => {
    setVideoFile(null);
    setSynergyFile(null);
    setGameName('');
    setGameDate('');
    setHomeTeam('');
    setAwayTeam('');
    setUploading(false);
    setUploadPhase('');
    setUploadProgress(0);
    setError(null);
  };

  const estimatedSegments = videoFile ? Math.ceil((videoFile.size / (1024 * 1024)) / 40) : 0; // rough: ~40MB per minute
  const estimatedTime = estimatedSegments * 30; // ~30s per segment

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-zinc-950 border border-white/[0.12] rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.08]">
          <h2 className="text-sm font-semibold text-white/90">Add New Game</h2>
          <button onClick={() => { onClose(); resetForm(); }} className="p-1 hover:bg-white/[0.06] rounded-lg transition-colors">
            <X size={16} className="text-white/40" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Video Drop Zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleVideoDrop}
            onClick={() => videoInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
              videoFile
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-white/[0.12] hover:border-white/[0.25] hover:bg-white/[0.02]'
            }`}
          >
            <input ref={videoInputRef} type="file" accept="video/mp4,video/*" onChange={handleVideoDrop} className="hidden" />
            {videoFile ? (
              <div className="flex items-center gap-3">
                <FileVideo size={20} className="text-amber-400" />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-xs font-medium text-white/80 truncate">{videoFile.name}</div>
                  <div className="text-[10px] text-white/40">{(videoFile.size / (1024 * 1024 * 1024)).toFixed(2)} GB</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setVideoFile(null); }} className="text-white/30 hover:text-white/60">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div>
                <Upload size={24} className="mx-auto text-white/20 mb-2" />
                <div className="text-xs text-white/50">Drop game video here or click to browse</div>
                <div className="text-[10px] text-white/25 mt-1">MP4, up to 4GB</div>
              </div>
            )}
          </div>

          {/* Synergy Drop Zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleSynergyDrop}
            onClick={() => synergyInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-3 text-center cursor-pointer transition-all ${
              synergyFile
                ? 'border-cyan-500/40 bg-cyan-500/5'
                : 'border-white/[0.12] hover:border-white/[0.25] hover:bg-white/[0.02]'
            }`}
          >
            <input ref={synergyInputRef} type="file" accept=".csv,.xml" onChange={handleSynergyDrop} className="hidden" />
            {synergyFile ? (
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={18} className="text-cyan-400" />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-xs font-medium text-white/80 truncate">{synergyFile.name}</div>
                  <div className="text-[10px] text-white/40">{(synergyFile.size / 1024).toFixed(0)} KB</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setSynergyFile(null); }} className="text-white/30 hover:text-white/60">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-center">
                <FileSpreadsheet size={16} className="text-white/20" />
                <span className="text-xs text-white/50">Drop Synergy CSV/XML</span>
              </div>
            )}
          </div>

          {/* Game Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Game Name</label>
              <input
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                placeholder="Cornell @ Princeton"
                className="w-full px-3 py-1.5 bg-zinc-900/80 border border-white/[0.1] rounded-lg text-xs text-white/80 placeholder-white/20 focus:border-amber-500/40 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Date</label>
              <input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="w-full px-3 py-1.5 bg-zinc-900/80 border border-white/[0.1] rounded-lg text-xs text-white/80 focus:border-amber-500/40 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Game ID Preview</label>
              <div className="px-3 py-1.5 bg-zinc-900/40 border border-white/[0.06] rounded-lg text-[10px] text-white/30 font-mono truncate">
                {generateGameId() || 'AUTO_GENERATED'}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Home Team</label>
              <input
                value={homeTeam}
                onChange={(e) => setHomeTeam(e.target.value)}
                placeholder="Princeton Tigers"
                className="w-full px-3 py-1.5 bg-zinc-900/80 border border-white/[0.1] rounded-lg text-xs text-white/80 placeholder-white/20 focus:border-amber-500/40 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Away Team</label>
              <input
                value={awayTeam}
                onChange={(e) => setAwayTeam(e.target.value)}
                placeholder="Cornell Big Red"
                className="w-full px-3 py-1.5 bg-zinc-900/80 border border-white/[0.1] rounded-lg text-xs text-white/80 placeholder-white/20 focus:border-amber-500/40 focus:outline-none"
              />
            </div>
          </div>

          {/* Time Estimate */}
          {videoFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/5 border border-amber-500/15 rounded-lg">
              <Clock size={14} className="text-amber-400/60" />
              <span className="text-[11px] text-white/50">
                Est. {estimatedSegments} segments ~ {formatTime(estimatedTime)} analysis time
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
              {error}
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {uploadPhase === 'done' ? (
                  <CheckCircle2 size={14} className="text-emerald-400" />
                ) : (
                  <Loader2 size={14} className="text-amber-400 animate-spin" />
                )}
                <span className="text-xs text-white/60">
                  {uploadPhase === 'uploading_video' && `Uploading video... ${uploadProgress}%`}
                  {uploadPhase === 'registering' && 'Registering game & starting pipeline...'}
                  {uploadPhase === 'done' && 'Pipeline started! You can navigate away.'}
                </span>
              </div>
              {uploadPhase === 'uploading_video' && (
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.08]">
          <button
            onClick={() => { onClose(); resetForm(); }}
            disabled={uploading}
            className="px-4 py-1.5 text-xs text-white/50 hover:text-white/80 transition-colors disabled:opacity-30"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || (!videoFile && !synergyFile)}
            className="px-4 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {uploading ? 'Processing...' : 'Start Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
}
